import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, Loader2, ExternalLink, CheckCircle2, Clock } from "lucide-react";
import DesktopLayout from "@/components/Layout/DesktopLayout";
import { api, formatApiError } from "@/api/client";

const stageLabel = (s) => (s || "—").replaceAll("_", " ");
const dt = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

export default function ProductionDesignWork() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all"); // all | pending | approved

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/project/production-design-work");
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const rows = data?.rows || [];
  const filtered = useMemo(() => {
    let out = rows;
    const needle = q.trim().toLowerCase();
    if (needle) {
      out = out.filter((r) =>
        [r.project_id, r.name, r.customer_name, r.sales_person_name, r.dealer_name]
          .filter(Boolean)
          .some((s) => String(s).toLowerCase().includes(needle))
      );
    }
    if (status === "pending") out = out.filter((r) => !r.drawing_approved_at);
    if (status === "approved") out = out.filter((r) => r.drawing_approved_at);
    return out;
  }, [rows, q, status]);

  const totals = data?.totals || { count: 0, pending: 0, approved: 0 };

  return (
    <DesktopLayout>
      <div className="max-w-[1500px] mx-auto p-3 sm:p-6 space-y-4" data-testid="prod-design-work-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="hk-tiny-label">Production Design</div>
            <h1 className="text-3xl font-black tracking-tight text-slate-900">My Work</h1>
            <div className="text-xs text-slate-500 mt-1">
              Every project you have received a production drawing brief for — with dates and approval status.
            </div>
          </div>
          <button type="button" onClick={load} disabled={loading}
            className="px-3 py-2 text-xs font-bold uppercase border border-slate-300 rounded-sm hover:bg-slate-50 disabled:opacity-50"
            data-testid="pdw-refresh">
            {loading ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : null} Refresh
          </button>
        </div>

        <div className="grid grid-cols-3 gap-3" data-testid="pdw-totals">
          <Tile label="Total projects" value={totals.count} icon={null} tone="#0f172a" />
          <Tile label="Pending approval" value={totals.pending} icon={Clock} tone="#c2410c" />
          <Tile label="Customer approved" value={totals.approved} icon={CheckCircle2} tone="#15803d" />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
            <input className="w-full border border-slate-300 rounded-sm pl-8 pr-3 py-2 text-sm"
              placeholder="Search project id · customer · sales / dealer"
              value={q} onChange={(e) => setQ(e.target.value)}
              data-testid="pdw-search" />
          </div>
          <div className="inline-flex rounded-sm overflow-hidden border border-slate-300">
            {[["all", "All"], ["pending", "Pending"], ["approved", "Approved"]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setStatus(k)}
                data-testid={`pdw-status-${k}`}
                className={`px-3 py-2 text-xs font-bold uppercase ${status === k ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-xs" data-testid="pdw-table">
            <thead className="bg-slate-50 border-b-2 border-slate-200">
              <tr className="text-left">
                <Th>Customer</Th>
                <Th>Project</Th>
                <Th>Sales / Dealer</Th>
                <Th>Drawing received</Th>
                <Th>Drawing approved</Th>
                <Th>Status</Th>
                <Th align="center">Link</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-8 text-slate-400">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-8 text-slate-400" data-testid="pdw-empty">
                  No projects match the current filters.
                </td></tr>
              ) : (
                filtered.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`pdw-row-${r.id}`}>
                    <td className="px-3 py-2">
                      <div className="font-bold text-slate-900 truncate max-w-[200px]">{r.customer_name || "—"}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium truncate max-w-[220px]">{r.name || "—"}</div>
                      <div className="font-mono text-[10px] text-slate-500 max-w-[220px] truncate">
                        {r.project_id || ""}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-slate-700 max-w-[150px] truncate">
                      {r.dealer_name || r.sales_person_name || <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-2 text-slate-600 text-[11px] whitespace-nowrap">
                      {dt(r.drawing_received_at)}
                    </td>
                    <td className="px-3 py-2 text-[11px] whitespace-nowrap">
                      {r.drawing_approved_at
                        ? <span className="text-emerald-700 font-bold">{dt(r.drawing_approved_at)}</span>
                        : <span className="text-slate-400">Pending</span>}
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-[10px] uppercase font-bold text-slate-600">{stageLabel(r.stage)}</span>
                      {r.sub_status && (
                        <div className="text-[9px] text-slate-400">{stageLabel(r.sub_status)}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <Link to={`/projects/${r.id}`}
                        className="inline-flex items-center gap-1 text-orange-700 hover:text-orange-900 font-bold text-[11px]"
                        data-testid={`pdw-open-${r.id}`}>
                        Open <ExternalLink className="w-3 h-3" />
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="text-[10px] text-slate-400">
          Drawing received = when payment was verified and the project entered the Production Design stage.
          Drawing approved = when the customer approved the production drawings.
        </div>
      </div>
    </DesktopLayout>
  );
}

function Th({ children, align = "left" }) {
  return (
    <th className={`px-3 py-2 font-bold uppercase text-[10px] tracking-wide text-slate-500 ${align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left"}`}>
      {children}
    </th>
  );
}

function Tile({ icon: Icon, label, value, tone }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5" style={tone ? { color: tone } : {}} />}
        <div className="hk-tiny-label">{label}</div>
      </div>
      <div className="text-2xl font-black tracking-tight mt-1"
        style={tone ? { color: tone } : {}}>
        {value}
      </div>
    </div>
  );
}
